import { useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { startVideoUpload, type VideoUploadProgress } from "@forento/ui/utilities/video"

import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import VideoUploader from "~/components/VideoUploader"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const UploadVideoPage: React.FC = () => {
	const { videoId } = useParams()
	const navigate = useNavigate()

	const { data: video, error } = query.video.get.useQuery({ videoId: Number(videoId) })
	const [progress, setProgress] = useState<VideoUploadProgress>()

	if (video === null || (video !== undefined && video.uploadCompleteDate !== null))
		return <Navigate to={routes.video.index()} />

	const handleUpload = async (file: File) => {
		if (video === undefined || video.host !== "bunny") return

		const startTime = Date.now()
		window.posthog?.capture("video_upload_started", { video_id: video.id, file_size: file.size })

		const videoUpload = await trpc.video.initializeUpload.query({ bunnyVideoId: video.bunnyVideoId })
		await startVideoUpload({
			file,
			bunnyVideoId: video.bunnyVideoId,
			libraryId: videoUpload.libraryId,
			signature: videoUpload.signature,
			onProgress: async progress => {
				setProgress(progress)
				if (progress.status === "completed") {
					const uploadDuration = Math.round((Date.now() - startTime) / 1000)
					const averageUploadSpeed = file.size / uploadDuration / 1024 / 1024
					await trpc.video.completeUpload.mutate({ videoId: video.id })
					window.posthog?.capture("video_upload_completed", {
						video_id: video.id,
						upload_duration: uploadDuration,
						average_upload_speed: averageUploadSpeed,
					})
					navigate(routes.video.index())
				}
			},
		})
	}

	return (
		<Layout>
			<PageBreadcrumb title="Upload" path={[{ title: "Videos", link: routes.video.index() }]} />
			{error ? (
				<p>Failed to load video.</p>
			) : video === undefined ? (
				<PartialLoadingPage />
			) : (
				<VideoUploader
					onUpload={handleUpload}
					progress={
						progress !== undefined
							? progress.status === "uploading"
								? progress.progress
								: progress.status === "completed"
									? 1
									: 0
							: null
					}
				/>
			)}
		</Layout>
	)
}

export default UploadVideoPage
